/**
 * TitlePoint Workflow Orchestration
 * Business logic for executing complete TitlePoint async workflows
 *
 * All TitlePoint searches follow a 3-step async pattern (MANUAL POLLING ONLY):
 * 1. CreateService/CreateService3/ResolveProperties – Returns RequestID
 * 2. Poll GetRequestSummaries until complete (MANUAL ONLY - NO AUTOPOLLING)
 * 3. GetResultById for each ResultID (MANUAL ONLY)
 */

import {
  buildCreateService3XML,
  buildCreateServiceXML,
  buildResolvePropertiesXML,
  buildGetRequestSummariesXML,
  buildGetResultByIdXML,
  callTitlePointAPI,
  parseSOAPResponse
} from './titlepointApi';

/**
 * Execute property search workflow using CreateService3
 * NO AUTOPOLLING - User must manually poll via Transaction History
 *
 * @param {Object} searchParams - Property search parameters
 * @param {Object} credentials - TitlePoint credentials
 * @param {Object} options - Workflow options (unused, kept for compatibility)
 * @returns {Promise<WorkflowResult>}
 */
export const executePropertySearchWorkflow = async (searchParams, credentials, options = {}) => {
  const workflowResult = {
    success: false,
    requestId: null,
    orderId: null,
    createResponse: null,
    pollAttempts: 0,
    pollComplete: false,
    resultIds: [],
    results: [],
    error: null,
    timing: {
      started: new Date().toISOString(),
      createCompleted: null,
      pollCompleted: null,
      resultsCompleted: null,
      totalDurationMs: null
    }
  };

  const startTime = Date.now();

  try {
    // Step 1: Create Service Request (ONLY - no autopolling)
    const createXML = buildCreateService3XML(searchParams, credentials);

    const createResponse = await callTitlePointAPI(
      credentials.serviceUrl,
      createXML,
      credentials,
      'http://www.TitlePoint.com/CreateService3'
    );

    const createResponseXML = createResponse.responseXML || createResponse.rawResponse;
    const createParsed = parseSOAPResponse(createResponseXML);

    workflowResult.timing.createCompleted = new Date().toISOString();
    workflowResult.createResponse = createParsed;

    // Debug logging
    console.log('CreateService3 parsed response:', JSON.stringify(createParsed, null, 2));

    if (!createParsed.success) {
      throw new Error(`CreateService3 failed: ${createParsed.error}`);
    }

    // Extract requestId and orderId
    const requestId = findInObject(createParsed.data, ['RequestID', 'requestId', 'requestID']);
    const orderId = findInObject(createParsed.data, ['OrderID', 'orderId', 'orderID']);

    console.log('Extracted IDs - requestId:', requestId, 'orderId:', orderId);
    console.log('Full parsed data:', JSON.stringify(createParsed.data, null, 2));

    if (!requestId) {
      throw new Error(`No requestId returned from CreateService3. Response structure: ${JSON.stringify(createParsed.data, null, 2)}`);
    }

    workflowResult.requestId = requestId;
    workflowResult.orderId = orderId;
    workflowResult.timing.totalDurationMs = Date.now() - startTime;

    // Mark as success - CreateService3 succeeded
    // User must manually poll from Transaction History
    workflowResult.success = true;

    return workflowResult;

  } catch (error) {
    workflowResult.success = false;
    workflowResult.error = error.message;
    workflowResult.timing.totalDurationMs = Date.now() - startTime;
    return workflowResult;
  }
};

/**
 * Execute title search workflow using CreateService
 * NO AUTOPOLLING - User must manually poll via Transaction History
 *
 * @param {Object} searchParams - Property search parameters
 * @param {Object} credentials - TitlePoint credentials
 * @param {Object} options - Workflow options (unused, kept for compatibility)
 * @returns {Promise<WorkflowResult>}
 */
export const executeTitleSearchWorkflow = async (searchParams, credentials, options = {}) => {
  const workflowResult = {
    success: false,
    requestId: null,
    orderId: null,
    createResponse: null,
    pollAttempts: 0,
    pollComplete: false,
    resultIds: [],
    results: [],
    error: null,
    timing: {
      started: new Date().toISOString(),
      createCompleted: null,
      pollCompleted: null,
      resultsCompleted: null,
      totalDurationMs: null
    }
  };

  const startTime = Date.now();

  try {
    // Step 1: Create Service Request (ONLY - no autopolling)
    const createXML = buildCreateServiceXML(searchParams, credentials);

    const createResponse = await callTitlePointAPI(
      credentials.serviceUrl,
      createXML,
      credentials,
      'http://www.TitlePoint.com/CreateService'
    );

    const createResponseXML = createResponse.responseXML || createResponse.rawResponse;
    const createParsed = parseSOAPResponse(createResponseXML);

    workflowResult.timing.createCompleted = new Date().toISOString();
    workflowResult.createResponse = createParsed;

    if (!createParsed.success) {
      throw new Error(`CreateService failed: ${createParsed.error}`);
    }

    // Extract requestId and orderId
    const requestId = findInObject(createParsed.data, ['RequestID', 'requestId', 'requestID']);
    const orderId = findInObject(createParsed.data, ['OrderID', 'orderId', 'orderID']);

    if (!requestId) {
      throw new Error('No requestId returned from CreateService');
    }

    workflowResult.requestId = requestId;
    workflowResult.orderId = orderId;
    workflowResult.timing.totalDurationMs = Date.now() - startTime;

    // Mark as success - CreateService succeeded
    // User must manually poll from Transaction History
    workflowResult.success = true;

    return workflowResult;

  } catch (error) {
    workflowResult.success = false;
    workflowResult.error = error.message;
    workflowResult.timing.totalDurationMs = Date.now() - startTime;
    return workflowResult;
  }
};

/**
 * Execute property resolution workflow using ResolveProperties
 * NO AUTOPOLLING - User must manually poll via Transaction History
 *
 * @param {Object} searchParams - Property search parameters
 * @param {Object} credentials - TitlePoint credentials
 * @param {Object} options - Workflow options (unused, kept for compatibility)
 * @returns {Promise<WorkflowResult>}
 */
export const executePropertyResolutionWorkflow = async (searchParams, credentials, options = {}) => {
  const workflowResult = {
    success: false,
    requestId: null,
    orderId: null,
    createResponse: null,
    pollAttempts: 0,
    pollComplete: false,
    resultIds: [],
    results: [],
    error: null,
    timing: {
      started: new Date().toISOString(),
      createCompleted: null,
      pollCompleted: null,
      resultsCompleted: null,
      totalDurationMs: null
    }
  };

  const startTime = Date.now();

  try {
    // Step 1: Create Service Request (ONLY - no autopolling)
    const createXML = buildResolvePropertiesXML(searchParams, credentials);

    const createResponse = await callTitlePointAPI(
      credentials.serviceUrl,
      createXML,
      credentials,
      'http://www.TitlePoint.com/ResolveProperties'
    );

    const createResponseXML = createResponse.responseXML || createResponse.rawResponse;
    const createParsed = parseSOAPResponse(createResponseXML);

    workflowResult.timing.createCompleted = new Date().toISOString();
    workflowResult.createResponse = createParsed;

    if (!createParsed.success) {
      throw new Error(`ResolveProperties failed: ${createParsed.error}`);
    }

    // Extract requestId
    const requestId = findInObject(createParsed.data, ['RequestID', 'requestId', 'requestID']);

    if (!requestId) {
      throw new Error('No requestId returned from ResolveProperties');
    }

    workflowResult.requestId = requestId;
    workflowResult.timing.totalDurationMs = Date.now() - startTime;

    // Mark as success - ResolveProperties succeeded
    // User must manually poll from Transaction History
    workflowResult.success = true;

    return workflowResult;

  } catch (error) {
    workflowResult.success = false;
    workflowResult.error = error.message;
    workflowResult.timing.totalDurationMs = Date.now() - startTime;
    return workflowResult;
  }
};

/**
 * Helper: Find a value in an object by trying multiple keys
 * @private
 */
function findInObject(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;

  // Try direct keys first
  for (const key of keys) {
    if (obj[key]) return obj[key];
  }

  // Search recursively
  for (const value of Object.values(obj)) {
    if (typeof value === 'object' && value !== null) {
      const found = findInObject(value, keys);
      if (found) return found;
    }
  }

  return null;
}

export default {
  executePropertySearchWorkflow,
  executeTitleSearchWorkflow,
  executePropertyResolutionWorkflow
};
